import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import "./App.css";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { useLibraryData } from "./hooks/useLibraryData";
import Login from "./components/Login";
import VideoList from "./components/VideoList";
import EditVideo from "./components/EditVideo";
import SplitVideo from "./components/SplitVideo";
import Members from "./components/Members";

const TOASTER_OPTIONS = { style: { fontSize: "17px" } };

function Organizer() {
  const { user, signOut } = useAuth();
  const { videos, tags, loading, error, reload, setLoading } = useLibraryData();
  const [screen, setScreen] = useState("list"); // list | edit | split | members
  const [selected, setSelected] = useState(null);

  // Keep the selected video in sync after a refresh.
  useEffect(() => {
    setSelected((prev) =>
      prev ? videos.find((v) => v.ratingKey === prev.ratingKey) || prev : prev
    );
  }, [videos]);

  const openEdit = (v) => {
    setSelected(v);
    setScreen("edit");
    window.scrollTo(0, 0);
  };
  const openSplit = (v) => {
    setSelected(v);
    setScreen("split");
    window.scrollTo(0, 0);
  };
  const backToList = () => {
    setScreen("list");
    setSelected(null);
    window.scrollTo(0, 0);
  };
  const afterSplit = async () => {
    await reload();
    backToList();
  };
  const retry = () => {
    setLoading(true);
    reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-white/50 animate-pulse" data-testid="loading">
          Loading your videos…
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <p className="text-2xl sm:text-3xl font-semibold text-white max-w-xl" data-testid="error-message">
          Can't reach the video server right now
        </p>
        <button
          onClick={retry}
          className="mt-7 h-14 px-9 rounded-2xl text-lg font-semibold text-white bg-[#0A84FF] hover:bg-[#409CFF] transition-colors"
          data-testid="retry-button"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <>
      {screen === "list" && (
        <VideoList
          videos={videos}
          onOpen={openEdit}
          user={user}
          onSignOut={signOut}
          onManagePeople={() => {
            setScreen("members");
            window.scrollTo(0, 0);
          }}
        />
      )}
      {screen === "members" && <Members onBack={backToList} />}
      {screen === "edit" && selected && (
        <EditVideo
          video={selected}
          allTags={tags}
          onBack={backToList}
          onSplit={openSplit}
          refresh={reload}
        />
      )}
      {screen === "split" && selected && (
        <SplitVideo video={selected} onBack={() => setScreen("edit")} onDone={afterSplit} />
      )}
    </>
  );
}

function Gate() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-white/50 animate-pulse">Loading…</p>
      </div>
    );
  }
  if (user === false) return <Login />;
  return <Organizer />;
}

export default function App() {
  return (
    <div className="App">
      <Toaster position="top-center" theme="dark" richColors toastOptions={TOASTER_OPTIONS} />
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </div>
  );
}
